(function () {
  'use strict'

  const CONTROLLER_KEY = '__redesignCwtvHomeController'
  const LOADER_KEY = '__redesignCwtvHomeLoader'
  const ROOT_SELECTOR = '[data-cwtv-home]'

  class RedesignCwtvHome {
    constructor (element) {
      this.element = element
      this.playerElement = this.element.querySelector('[data-player]')
      this.playPauseButton = this.element.querySelector('[data-cwtv-play-pause]')
      this.playIcon = this.element.querySelector('[data-cwtv-play-icon]')
      this.pauseIcon = this.element.querySelector('[data-cwtv-pause-icon]')
      this.muteButton = this.element.querySelector('[data-cwtv-mute]')
      this.mutedIcon = this.element.querySelector('[data-cwtv-muted-icon]')
      this.soundIcon = this.element.querySelector('[data-cwtv-sound-icon]')
      this.label = this.element.querySelector('[data-cwtv-label]')
      this.videos = this.getVideos()
      this.active = this.element.dataset.active || (this.videos[0] && this.videos[0].id)
      this.reducedMotion = Boolean(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
      this.mediaElement = null
      this.player = null
      this.paused = true
      this.playerStateReady = false
      this.isMuted = true

      this.handleClick = this.handleClick.bind(this)
      this.updatePlayerState = this.updatePlayerState.bind(this)

      this.init()
    }

    get videoIds () {
      return this.videos.map(video => video.id)
    }

    get currentIndex () {
      const index = this.videoIds.indexOf(this.active)

      return index > -1 ? index : 0
    }

    get activeVideo () {
      return this.videos.find(video => video.id === this.active) || this.videos[0] || null
    }

    init () {
      if (!this.playerElement) return

      if (this.reducedMotion) {
        this.element.classList.add('redesign-cwtv--reduced-motion')
      }

      this.element.addEventListener('click', this.handleClick)
      this.mediaElement = this.playerElement.querySelector('[data-media]')

      if (this.mediaElement) {
        this.initPlayer(this.mediaElement)
      } else {
        this.playerStateReady = true
        this.updateControls()
      }
    }

    destroy () {
      this.element.removeEventListener('click', this.handleClick)
      this.destroyPlayer()
      delete this.element[CONTROLLER_KEY]
    }

    getVideos () {
      try {
        const encodedVideos = this.element.dataset.videos || '%5B%5D'

        return JSON.parse(decodeURIComponent(encodedVideos.replace(/\+/g, ' ')))
      } catch (error) {
        return []
      }
    }

    handleClick (event) {
      const previousButton = event.target.closest('[data-prev]')
      const nextButton = event.target.closest('[data-next]')
      const playPauseButton = event.target.closest('[data-cwtv-play-pause]')
      const muteButton = event.target.closest('[data-cwtv-mute]')

      if (previousButton && this.element.contains(previousButton)) {
        event.preventDefault()
        this.markChannelInteraction()
        this.stepVideo(-1)

        return
      }

      if (nextButton && this.element.contains(nextButton)) {
        event.preventDefault()
        this.markChannelInteraction()
        this.stepVideo(1)

        return
      }

      if (playPauseButton && this.element.contains(playPauseButton)) {
        this.togglePlayback()

        return
      }

      if (muteButton && this.element.contains(muteButton)) {
        this.toggleMute()
      }
    }

    markChannelInteraction () {
      this.element.classList.add('redesign-cwtv--chan-used')
    }

    stepVideo (step) {
      if (!this.videoIds.length) return

      const nextIndex = (this.currentIndex + step + this.videoIds.length) % this.videoIds.length

      this.active = this.videoIds[nextIndex]
      this.renderActiveVideo()
    }

    renderActiveVideo () {
      if (!this.playerElement || !this.activeVideo) return

      this.destroyPlayer()

      for (const media of this.playerElement.querySelectorAll('[data-media]')) {
        media.remove()
      }

      this.mediaElement = null
      this.paused = true
      this.playerStateReady = false

      if (this.activeVideo.type === 'mp4') {
        this.renderMp4()
      } else {
        this.renderEmbed()
      }

      if (!this.mediaElement) {
        this.playerStateReady = true
      }

      this.updateControls()
    }

    renderMp4 () {
      if (!this.activeVideo.url) return

      const video = document.createElement('video')
      const source = document.createElement('source')

      video.className = 'redesign-cwtv__video'
      video.dataset.media = ''
      video.loop = true
      video.muted = this.isMuted
      video.playsInline = true
      video.preload = this.reducedMotion ? 'metadata' : 'auto'
      video.setAttribute('playsinline', '')

      if (!this.reducedMotion) {
        video.autoplay = true
        video.setAttribute('autoplay', '')
      }

      if (this.activeVideo.poster) {
        video.poster = this.activeVideo.poster
      }

      if (this.activeVideo.label) {
        video.setAttribute('aria-label', this.activeVideo.label)
      }

      source.src = this.activeVideo.url
      source.type = 'video/mp4'
      video.appendChild(source)
      this.insertMedia(video)
      this.mediaElement = video
      this.initPlayer(video)
    }

    renderEmbed () {
      const videoSource = this.activeVideo.type === 'youtube'
        ? this.getYouTubeId(this.activeVideo.url)
        : this.getVimeoSource(this.activeVideo.url)

      if (!videoSource) return

      const embed = document.createElement('div')

      embed.className = `redesign-cwtv__video redesign-cwtv__video--embed redesign-cwtv__video--${this.activeVideo.type}`
      embed.dataset.media = ''
      embed.dataset.plyrProvider = this.activeVideo.type
      embed.dataset.plyrEmbedId = videoSource

      this.insertMedia(embed)
      this.mediaElement = embed
      this.initPlayer(embed)
    }

    insertMedia (media) {
      const controls = this.playerElement.querySelector('.redesign-cwtv__controls')

      if (controls) {
        this.playerElement.insertBefore(media, controls)

        return
      }

      this.playerElement.appendChild(media)
    }

    initPlayer (media) {
      if (!window.Plyr) {
        this.bindNativeMedia(media)

        return
      }

      const player = new window.Plyr(media, {
        autoplay: !this.reducedMotion,
        clickToPlay: false,
        controls: [],
        keyboard: {
          focused: false,
          global: false
        },
        loop: {
          active: true
        },
        muted: this.isMuted,
        playsinline: true,
        youtube: {
          noCookie: true,
          rel: 0,
          mute: this.isMuted ? 1 : 0
        },
        vimeo: {
          dnt: true
        }
      })

      this.player = player
      player.on('play', this.updatePlayerState)
      player.on('pause', this.updatePlayerState)
      player.muted = this.isMuted
      player.on('ready', () => {
        if (this.player !== player) return

        player.muted = this.isMuted

        if (this.reducedMotion) {
          this.pause()
          this.updatePlayerState()

          return
        }

        this.play()
      })
      this.updateControls()
    }

    bindNativeMedia (media) {
      if (!(media instanceof window.HTMLMediaElement)) {
        this.updateControls()

        return
      }

      media.addEventListener('play', this.updatePlayerState)
      media.addEventListener('pause', this.updatePlayerState)
      this.updatePlayerState()

      if (this.reducedMotion) {
        this.pause()
      } else {
        this.play()
      }
    }

    destroyPlayer () {
      if (this.mediaElement instanceof window.HTMLMediaElement) {
        this.mediaElement.removeEventListener('play', this.updatePlayerState)
        this.mediaElement.removeEventListener('pause', this.updatePlayerState)
      }

      if (!this.player) return

      const player = this.player

      try {
        player.off('play', this.updatePlayerState)
        player.off('pause', this.updatePlayerState)
        player.destroy()
      } catch {
        // Plyr provider teardown can fail during rapid source changes.
      } finally {
        if (this.player === player) {
          this.player = null
        }
      }
    }

    updatePlayerState () {
      const media = this.player || this.mediaElement

      if (media && typeof media.paused === 'boolean') {
        this.paused = media.paused
        this.playerStateReady = true
      }

      this.updateControls()
    }

    togglePlayback () {
      if (this.paused) {
        this.play()

        return
      }

      this.pause()
    }

    play () {
      const media = this.player || this.mediaElement

      if (!media || !media.play) return

      const playPromise = media.play()

      if (playPromise && playPromise.then && playPromise.catch) {
        playPromise.then(() => {
          if ((this.player || this.mediaElement) !== media) return

          this.updatePlayerState()
        }).catch(() => {
          if ((this.player || this.mediaElement) !== media) return

          this.paused = true
          this.playerStateReady = true
          this.updateControls()
        })
      } else {
        window.requestAnimationFrame(() => {
          if ((this.player || this.mediaElement) !== media) return

          this.updatePlayerState()
        })
      }
    }

    pause () {
      const media = this.player || this.mediaElement

      if (!media || !media.pause) return

      media.pause()
    }

    toggleMute () {
      this.isMuted = !this.isMuted

      if (this.player) {
        this.player.muted = this.isMuted
      } else if (this.mediaElement instanceof window.HTMLMediaElement) {
        this.mediaElement.muted = this.isMuted
      }

      this.updateControls()
    }

    updateControls () {
      if (this.playPauseButton && this.playerStateReady) {
        const label = this.paused
          ? this.playPauseButton.dataset.playLabel
          : this.playPauseButton.dataset.pauseLabel

        if (label) {
          this.playPauseButton.setAttribute('aria-label', label)
        }
      }

      if (this.playIcon && this.playerStateReady) {
        this.playIcon.hidden = !this.paused
      }

      if (this.pauseIcon && this.playerStateReady) {
        this.pauseIcon.hidden = this.paused
      }

      if (this.muteButton) {
        this.muteButton.classList.toggle('redesign-cwtv__control--active', !this.isMuted)
      }

      if (this.mutedIcon) {
        this.mutedIcon.hidden = !this.isMuted
      }

      if (this.soundIcon) {
        this.soundIcon.hidden = this.isMuted
      }

      if (this.label) {
        const label = this.activeVideo && this.activeVideo.label

        this.label.textContent = label || ''
        this.label.hidden = !label
      }
    }

    getYouTubeId (url = '') {
      const value = url.trim()

      if (!value) return ''

      if (/^[A-Za-z0-9_-]{6,}$/.test(value) && value.indexOf('http') !== 0) {
        return value
      }

      const patterns = [
        /[?&]v=([^&#]+)/,
        /youtu\.be\/([^?&#/]+)/,
        /youtube\.com\/embed\/([^?&#/]+)/,
        /youtube\.com\/shorts\/([^?&#/]+)/
      ]

      for (const pattern of patterns) {
        const match = value.match(pattern)

        if (match && match[1]) {
          return match[1]
        }
      }

      return ''
    }

    getVimeoSource (url = '') {
      const value = url.trim()

      if (!value) return ''

      if (/^[0-9]+$/.test(value)) {
        return value
      }

      const vimeoUrlPattern = /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/)?[0-9]+(?:\/[A-Za-z0-9]+)?(?:[?#].*)?$/

      return vimeoUrlPattern.test(value) ? value : ''
    }
  }

  const init = (root = document) => {
    const elements = []

    if (root.matches && root.matches(ROOT_SELECTOR)) {
      elements.push(root)
    }

    if (root.querySelectorAll) {
      elements.push(...root.querySelectorAll(ROOT_SELECTOR))
    }

    for (const element of elements) {
      if (!element[CONTROLLER_KEY]) {
        element[CONTROLLER_KEY] = new RedesignCwtvHome(element)
      }
    }
  }

  if (window[LOADER_KEY]) {
    window[LOADER_KEY].init(document)

    return
  }

  window[LOADER_KEY] = { init }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true })
  } else {
    init(document)
  }

  document.addEventListener('shopify:section:load', event => init(event.target))
  document.addEventListener('shopify:section:unload', event => {
    const element = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector(ROOT_SELECTOR)

    if (element && element[CONTROLLER_KEY]) {
      element[CONTROLLER_KEY].destroy()
    }
  })
})()
